import { useCallback, useState } from 'react'
import { CircleAlert, Download } from 'lucide-react'
import { useV1ReportingPollResponsesList, v1ReportingPollResponsesExportRetrieve } from '@/api/client'
import { type PollResponseReportRow, type V1ReportingPollResponsesListParams } from '@/api/model'
import { DataTable, type Column } from '@/components/data-table'
import {
  EMPTY_POLL_RESPONSE_REPORT_FILTERS,
  POLL_RESPONSE_FILTER_ALL as ALL,
  type PollResponseReportFilterValues,
} from '@/components/reporting/poll-response-report-filter-values'
import { PollResponsesReportFilters } from '@/components/reporting/poll-responses-report-filters'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { filenameFromDisposition, saveBlob } from '@/lib/download-file'
import { queryHasHttpError } from '@/lib/query-state'
import { formatReportDateTime, omitPagination } from '@/lib/reporting-budget-entries'
import { hasPollResponseReportFilters } from '@/lib/reporting-poll-responses'

const PAGE_SIZE = 25

const columns: Column<PollResponseReportRow>[] = [
  {
    header: 'Uczeń',
    className: 'min-w-32 whitespace-normal',
    cell: (row) => <span className="font-mono text-xs text-muted-foreground">{row.anonymous_student_id}</span>,
  },
  {
    header: 'Szkoła / klasa',
    sortKey: 'school_name',
    className: 'min-w-40 whitespace-normal',
    cell: (row) => (
      <div className="flex max-w-64 flex-col gap-0.5">
        <span className="font-medium">{row.school_name ?? '—'}</span>
        <span className="text-xs text-muted-foreground">
          {[row.class_name, row.school_code].filter(Boolean).join(' · ') || '—'}
        </span>
      </div>
    ),
  },
  {
    header: 'Ankieta / pytanie',
    sortKey: 'poll_name',
    className: 'min-w-48 whitespace-normal',
    cell: (row) => (
      <div className="flex max-w-96 flex-col gap-0.5">
        <span className="font-medium">{row.poll_name}</span>
        <span className="line-clamp-2 text-xs text-muted-foreground" title={row.question_text}>
          {row.question_text}
        </span>
        <span className="text-xs text-muted-foreground">{row.question_type_label}</span>
      </div>
    ),
  },
  {
    header: 'Odpowiedź',
    sortKey: 'status',
    className: 'min-w-40 whitespace-normal',
    cell: (row) =>
      row.answer !== null ? (
        <span>{row.answer}</span>
      ) : (
        <Badge variant="outline">{row.status_label}</Badge>
      ),
  },
  {
    header: 'Data',
    sortKey: 'submitted_at',
    cell: (row) => formatReportDateTime(row.submitted_at),
  },
]

export function PollResponsesReport() {
  const [downloading, setDownloading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState('-submitted_at')
  const [filters, setFilters] = useState<PollResponseReportFilterValues>(
    EMPTY_POLL_RESPONSE_REPORT_FILTERS,
  )

  const params = {
    page,
    page_size: PAGE_SIZE,
    search: search.trim() || undefined,
    ordering:
      ordering === ''
        ? undefined
        : (ordering as NonNullable<V1ReportingPollResponsesListParams['ordering']>),
    poll_id: filters.pollId === ALL ? undefined : filters.pollId,
    school_id: filters.schoolId === ALL ? undefined : Number(filters.schoolId),
    class_id: filters.classId === ALL ? undefined : Number(filters.classId),
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined,
    question_type:
      filters.questionType === ALL
        ? undefined
        : (filters.questionType as NonNullable<V1ReportingPollResponsesListParams['question_type']>),
    status:
      filters.status === ALL
        ? undefined
        : (filters.status as NonNullable<V1ReportingPollResponsesListParams['status']>),
  }

  const query = useV1ReportingPollResponsesList(params)
  const data = query.data?.status === 200 ? query.data.data : undefined

  const filtering = hasPollResponseReportFilters({ search, ...filters })

  const downloadVisible = async () => {
    setFailed(false)
    setDownloading(true)
    try {
      const response = await v1ReportingPollResponsesExportRetrieve(omitPagination(params))
      if (response.status !== 200) {
        setFailed(true)
        return
      }
      const disposition = response.headers.get('content-disposition')
      saveBlob(response.data, filenameFromDisposition(disposition, 'odpowiedzi-ankiety.xlsx'))
    } catch {
      setFailed(true)
    } finally {
      setDownloading(false)
    }
  }

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [])

  const handleFiltersChange = useCallback((value: PollResponseReportFilterValues) => {
    setFilters(value)
    setPage(1)
  }, [])

  return (
    <section aria-labelledby="poll-responses-report-title" className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="poll-responses-report-title" className="font-heading text-xl font-semibold">
            Odpowiedzi w ankietach
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Anonimowe odpowiedzi uczniów na pytania ankiet, z wyszukiwaniem i filtrowaniem.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data ? (
            <Badge variant="secondary" className="w-fit">
              {data.count === 1 ? '1 odpowiedź' : `${data.count} odpowiedzi`}
            </Badge>
          ) : null}
          <Button onClick={downloadVisible} disabled={downloading} variant="outline">
            {downloading ? <Spinner data-icon="inline-start" /> : <Download data-icon="inline-start" />}
            {downloading ? 'Przygotowuję plik…' : 'Eksportuj widoczne (XLSX)'}
          </Button>
        </div>
      </div>

      {failed ? (
        <Alert variant="destructive" className="max-w-2xl">
          <CircleAlert />
          <AlertTitle>Nie udało się pobrać pliku</AlertTitle>
          <AlertDescription>Spróbuj ponownie za chwilę.</AlertDescription>
        </Alert>
      ) : null}

      <PollResponsesReportFilters
        search={search}
        onSearchChange={handleSearchChange}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <DataTable
        columns={columns}
        rows={data?.results ?? []}
        rowKey={(row) => `${row.id}`}
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        pageSize={PAGE_SIZE}
        search={search}
        onSearchChange={handleSearchChange}
        hideSearch
        ordering={ordering}
        onOrderingChange={(value) => {
          setOrdering(value)
          setPage(1)
        }}
        isLoading={query.isLoading}
        isError={queryHasHttpError(query)}
        errorMessage="Nie udało się pobrać odpowiedzi ankiet. Spróbuj ponownie."
        onRetry={() => void query.refetch()}
        emptyMessage={
          filtering
            ? 'Brak odpowiedzi spełniających wybrane kryteria.'
            : 'Uczniowie nie odpowiedzieli jeszcze na żadną ankietę.'
        }
        countNoun={['odpowiedź', 'odpowiedzi']}
      />
    </section>
  )
}
